import * as React from "react";
import clsx from "clsx";
import { Filter, X } from "lucide-react";
import { useToggle } from "react-use";
import { DatePicker } from "@/lib/cjs-interop";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip } from "@/components/ui/tooltip";
import {
  REVIEW_VERDICT_OPTIONS,
  TRAFFIC_LIGHT_OPTIONS,
  TRAFFIC_LIGHT_VARIANT,
} from "@/modules/snapshots/businessReports.types";
import { ReviewVerdictBadge } from "@/modules/snapshots/grid/reviewVerdictBadge";
import { ColumnFilterValue, ColumnKind, isColumnFilterActive } from "@/modules/snapshots/grid/sortAndFilter";
import "react-datepicker/dist/react-datepicker.css";

interface Props {
  columnName: string;
  columnKind: ColumnKind;
  value: ColumnFilterValue | undefined;
  onChange: (columnName: string, value: ColumnFilterValue | undefined) => void;
  showReviewSection?: boolean;
}

type FilterInputType = ColumnFilterValue["type"];

const inputTypeFor = (kind: ColumnKind): FilterInputType => {
  if (kind === "date") return "date";
  if (kind === "rating") return "rating";
  return "text";
};

const defaultValueFor = (kind: ColumnKind): ColumnFilterValue => {
  const type = inputTypeFor(kind);
  if (type === "date") return { type: "date", startDate: null, endDate: null, excludeNA: false };
  if (type === "rating") return { type: "rating", ratings: [], excludeNA: false };
  return { type: "text", value: "", excludeNA: false };
};

const matchesKind = (value: ColumnFilterValue | undefined, kind: ColumnKind): value is ColumnFilterValue =>
  !!value && value.type === inputTypeFor(kind);

interface RangeDateFieldProps {
  label: string;
  selected: Date | null;
  otherValue: Date | null;
  bound: "start" | "end";
  placeholder: string;
  onChange: (date: Date | null) => void;
}

const RangeDateField: React.FC<RangeDateFieldProps> = ({
  label,
  selected,
  otherValue,
  bound,
  placeholder,
  onChange,
}) => (
  <div className="space-y-1">
    <Label className="text-xs text-gray-500">{label}</Label>
    <DatePicker
      selected={selected}
      onChange={onChange}
      maxDate={bound === "start" ? otherValue || undefined : undefined}
      minDate={bound === "end" ? otherValue || undefined : undefined}
      dateFormat="PPP"
      isClearable
      placeholderText={placeholder}
      className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs"
      showYearDropdown
      showMonthDropdown
      dropdownMode="select"
    />
  </div>
);

export const ColumnFilterPopover: React.FC<Props> = ({
  columnName,
  columnKind,
  value,
  onChange,
  showReviewSection = false,
}) => {
  const [open, toggleOpen] = useToggle(false);
  const excludeNAId = React.useId();
  const [draft, setDraft] = React.useState<ColumnFilterValue>(() =>
    matchesKind(value, columnKind) ? value : defaultValueFor(columnKind),
  );

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setDraft(matchesKind(value, columnKind) ? value : defaultValueFor(columnKind));
    }
    toggleOpen(next);
  };

  const isActive = isColumnFilterActive(value);
  const inputType = inputTypeFor(columnKind);

  const handleApply = () => {
    onChange(columnName, isColumnFilterActive(draft) ? draft : undefined);
    toggleOpen(false);
  };

  const handleClear = () => {
    setDraft(defaultValueFor(columnKind));
    onChange(columnName, undefined);
    toggleOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleApply();
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Tooltip text="Filter" className="rounded bg-black text-xs text-white">
        <PopoverTrigger asChild>
          <button
            type="button"
            onClick={event => event.stopPropagation()}
            aria-label={isActive ? `Edit filter for ${columnName}` : `Filter ${columnName}`}
            className={clsx(
              "ml-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded transition-colors",
              isActive
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : "text-gray-400 hover:bg-gray-200 hover:text-gray-600",
            )}
          >
            <Filter className={clsx("h-3.5 w-3.5", isActive && "fill-current")} />
          </button>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent
        align="start"
        className="w-72 p-3"
        onClick={event => event.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className="mb-3 flex items-center justify-between gap-2">
          <Label className="truncate text-xs font-semibold uppercase text-gray-500">Filter {columnName}</Label>
          {isActive && (
            <button
              type="button"
              onClick={handleClear}
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-100"
            >
              <X className="h-3 w-3" />
              Clear
            </button>
          )}
        </div>

        {inputType === "text" && draft.type === "text" && (
          <Input
            autoFocus
            value={draft.value}
            placeholder="Contains…"
            onChange={event => setDraft(prev => (prev.type === "text" ? { ...prev, value: event.target.value } : prev))}
          />
        )}

        {inputType === "date" && draft.type === "date" && (
          <div className="grid grid-cols-2 gap-2">
            <RangeDateField
              label="From"
              selected={draft.startDate}
              otherValue={draft.endDate}
              bound="start"
              placeholder="Start date"
              onChange={date =>
                setDraft(prev => {
                  if (prev.type !== "date") return prev;
                  return {
                    ...prev,
                    startDate: date,
                    endDate: date && prev.endDate && prev.endDate < date ? null : prev.endDate,
                  };
                })
              }
            />
            <RangeDateField
              label="To"
              selected={draft.endDate}
              otherValue={draft.startDate}
              bound="end"
              placeholder="End date"
              onChange={date =>
                setDraft(prev => {
                  if (prev.type !== "date") return prev;
                  return {
                    ...prev,
                    startDate: date && prev.startDate && prev.startDate > date ? null : prev.startDate,
                    endDate: date,
                  };
                })
              }
            />
          </div>
        )}

        {inputType === "rating" && draft.type === "rating" && (
          <div className="flex flex-wrap gap-2">
            {TRAFFIC_LIGHT_OPTIONS.map(option => {
              const selected = draft.ratings.includes(option.value);
              const toggle = () =>
                setDraft(prev => {
                  if (prev.type !== "rating") return prev;
                  return {
                    ...prev,
                    ratings: selected ? prev.ratings.filter(r => r !== option.value) : [...prev.ratings, option.value],
                  };
                });
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={toggle}
                  className={clsx(
                    "rounded-full border px-2 py-0.5 text-xs transition",
                    selected ? "border-transparent ring-2 ring-coral ring-offset-1" : "border-gray-200 opacity-70",
                  )}
                >
                  <Badge variant={TRAFFIC_LIGHT_VARIANT[option.value]}>{option.label}</Badge>
                </button>
              );
            })}
          </div>
        )}

        {showReviewSection && (
          <div className="mt-3">
            <Label className="text-xs font-semibold uppercase text-gray-500">Review verdict</Label>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {REVIEW_VERDICT_OPTIONS.map(option => {
                const selected = (draft.reviewVerdicts ?? []).includes(option.value);
                const toggle = () =>
                  setDraft(prev => {
                    const current = prev.reviewVerdicts ?? [];
                    return {
                      ...prev,
                      reviewVerdicts: selected ? current.filter(v => v !== option.value) : [...current, option.value],
                    };
                  });
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={toggle}
                    aria-pressed={selected}
                    className={clsx(
                      "rounded-full border px-1 py-0.5 text-xs transition",
                      selected ? "border-transparent ring-2 ring-coral ring-offset-1" : "border-gray-200 opacity-70",
                    )}
                  >
                    <ReviewVerdictBadge verdict={option.value} />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-3 flex items-center gap-2">
          <Checkbox
            id={excludeNAId}
            checked={draft.excludeNA === true}
            onCheckedChange={checked =>
              setDraft(prev => ({
                ...prev,
                excludeNA: checked === true,
              }))
            }
          />
          <Label htmlFor={excludeNAId} className="cursor-pointer text-sm text-gray-700">
            Exclude N/A
          </Label>
        </div>

        <div className="mt-3 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => toggleOpen(false)}>
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
